import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { IconLoader2 } from "@tabler/icons-react";

import { focusStyles } from "@beweegbaas/ui/lib/focus-styles";
import { cn } from "@beweegbaas/ui/lib/utils";
import { isValidElement, type ComponentPropsWithRef } from "react";
import { useSpinDelay } from "spin-delay";

const buttonVariants = cva(
  `px-5 py-2 button inset-shadow-sm inset-shadow-white/30 text-nowrap 
  [&_svg]:shrink-0 relative font-heading font-semibold text-sm justify-center 
  inline-flex gap-2 items-center rounded-lg supports-squircle:rounded-full 
  squircle group-[.group-button]:rounded-none group-[.group-button]:first:rounded-l-full 
  group-[.group-button]:last:rounded-r-full active:scale-98 active:duration-150 
  aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 
  aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 
  bg-clip-padding aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 
  whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 
  disabled:grayscale disabled:cursor-not-allowed [&_svg]:pointer-events-none 
  [&_svg]:shrink-0 group/button select-none
  data-[popup-open]:grayscale`,
  {
    variants: {
      variant: {
        default: [
          "bg-linear-to-br from-blue-500 to-blue-600 shadow active:from-blue-800 active:to-blue-900 text-white",
          "data-popup-open:opacity-50! hover:from-blue-500 hover:to-blue-600/90 data-[popup-open]:from-blue-500 data-[popup-open]:to-blue-600/90",
          "dark:bg-none dark:text-blue-900 dark:bg-blue-50 dark:active:bg-blue-300",
          "dark:hover:bg-blue-200 dark:data-[popup-open]:bg-blue-200",
        ],
        outline: "bg-transparent border border-border",
        secondary: [
          "text-foreground shadow-sm ring-1 ring-border",
          "bg-linear-to-br from-neutral-50 to-neutral-100",
          "hover:from-neutral-200 hover:to-neutral-200 data-[popup-open]:from-neutral-200 data-[popup-open]:to-neutral-200",
          "dark:bg-none dark:bg-black/20 dark:active:bg-black/40",
          "dark:hover:bg-black/30 dark:data-[popup-open]:bg-black/30",
        ],
        "secondary-light": [
          "shadow-sm ring-1 ring-border",
          "bg-linear-to-br from-white/50 to-black/5",
          "hover:from-white/80 hover:to-black/10 data-[popup-open]:from-white/80 data-[popup-open]:to-black/10",
          "dark:bg-none dark:bg-white/10 dark:hover:bg-white/20 dark:data-[popup-open]:bg-white/20",
        ],
        "secondary-transparent": [
          "shadow-sm ring-1 ring-border",
          "bg-linear-to-br from-black/3 to-black/5 dark:text-white",
          "hover:from-white/50 hover:to-black/5 data-[popup-open]:from-white/50 data-[popup-open]:to-black/5",
          "dark:bg-black/20 dark:active:bg-black/40",
          "dark:hover:bg-black/30 dark:data-[popup-open]:bg-black/30",
        ],
        ghost: [
          "bg-transparent",
          "hover:bg-muted data-[popup-open]:bg-muted",
          "dark:bg-transparent",
          "dark:hover:bg-muted/20 dark:data-[popup-open]:bg-muted/20",
        ],
        destructive: [
          "bg-red-700 active:bg-red-900 text-white",
          "hover:bg-red-800 data-[popup-open]:bg-red-800",
          "dark:text-red-900 dark:bg-red-50 dark:active:bg-red-300",
          "dark:hover:bg-red-200 dark:data-[popup-open]:bg-red-200",
        ],
        "destructive-light": [
          "bg-red-700/20 active:bg-red-700/40 text-red-900",
          "hover:bg-red-700/30 data-[popup-open]:bg-red-700/30",
          "dark:text-red-300",
        ],
        flat: [
          "shadow-none bg-black/10",
          "hover:bg-black/20 data-[popup-open]:bg-black/20 dark:bg-black/20 blue:text-white! blue:inset-shadow-none",
        ],
        light: [
          "bg-linear-to-br from-white/60 to-white/40 text-black ring-1 ring-border",
          "hover:from-white/90 hover:to-white/40 data-[popup-open]:from-white/90 data-[popup-open]:to-white/40",
          "hover:shadow-sm data-[popup-open]:shadow-sm",
        ],
        success: [
          "bg-green-600 active:bg-green-800 text-white",
          "hover:bg-green-700 data-[popup-open]:bg-green-700",
        ],
        info: [
          "bg-linear-to-br from-blue-100/80 to-blue-200 active:from-blue-200/80 active:to-blue-200 text-blue-700",
          "hover:from-blue-50 hover:to-blue-200 data-[popup-open]:from-blue-50 data-[popup-open]:to-blue-200",
          "dark:bg-none dark:bg-blue-800/10 dark:text-white",
        ],
        special: [
          "shadow-sm text-white",
          "bg-linear-to-br from-blue-600 to-orange-400",
          "hover:from-blue-500 hover:to-orange-400 data-[popup-open]:from-blue-500 data-[popup-open]:to-orange-400",
        ],
        orange: [
          "shadow-sm text-white",
          "bg-linear-to-br from-orange-600/30 to-orange-400",
          "hover:from-orange-600/40 hover:to-orange-400 data-[popup-open]:from-orange-600/40 data-[popup-open]:to-orange-400",
        ],
        reset: "bg-transparent p-0 h-auto! outline-0 inset-shadow-none",
        link: [
          "text-primary underline-offset-4 font-sans font-normal",
          "hover:underline data-[popup-open]:underline",
        ],
        "link-muted": ["text-muted-foreground hover:text-foreground font-sans font-normal"],
      },
      size: {
        default:
          "h-9 gap-1.5 px-4 py-2 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xxs: "h-5 gap-1 rounded-[min(var(--radius-md),8px)] px-2 py-0.5 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 py-0.5 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 rounded-[min(var(--radius-md),10px)] px-3 py-1 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5",
        lg: "h-10 gap-1.5 px-6 py-3 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        xl: "h-11 gap-2 md:px-10 md:py-4 text-base has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5",
        "2xl":
          "h-13 gap-2.5 md:px-10 md:py-6 text-lg has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        "3xl":
          "h-15 gap-3 md:px-12 md:py-8 text-xl has-data-[icon=inline-end]:pr-4.5 has-data-[icon=inline-start]:pl-4.5",
        icon: "size-9 min-w-9 p-0",
        "icon-xs":
          "size-6 min-w-6 p-0 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm":
          "size-5 min-w-5 p-0 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-md",
        "icon-lg": "size-10 min-w-10 p-0",
        "icon-xl": "size-11 min-w-11 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = ComponentPropsWithRef<typeof ButtonPrimitive> &
  VariantProps<typeof buttonVariants> & {
    type?: "button" | "submit" | "reset";
    form?: string;
    /** Async action in flight: blocks clicks right away, shows the spinner after a short delay. */
    isPending?: boolean;
    contentClassName?: string;
  };

function Button({
  className,
  variant = "default",
  size = "default",
  isPending = false,
  disabled = false,
  focusableWhenDisabled,
  contentClassName,
  children,
  nativeButton: nativeButtonProp,
  ...props
}: ButtonProps) {
  // Spinner waits for spin-delay's threshold so quick actions don't flicker; AND with the raw
  // signal because spin-delay can latch on when its minDuration timer is lost.
  const showSpinner = useSpinDelay(isPending, { ssr: false }) && isPending;
  const shouldUseNativeButton = (() => {
    if (nativeButtonProp !== undefined) {
      return nativeButtonProp;
    }

    if (!("render" in props) || !props.render) {
      return true;
    }

    if (!isValidElement(props.render)) {
      return false;
    }

    return props.render.type === "button";
  })();

  return (
    <ButtonPrimitive
      data-slot="button"
      data-pending={showSpinner || undefined}
      aria-busy={isPending || undefined}
      className={cn(buttonVariants({ variant, size }), focusStyles, className)}
      {...props}
      // Pending blocks clicks immediately but keeps focus: base-ui renders aria-disabled instead of
      // the disabled attribute when focusableWhenDisabled is set. The `disabled:` variant skips
      // aria-busy elements (see index.css), so no disabled styling shows while pending.
      disabled={disabled || isPending}
      focusableWhenDisabled={focusableWhenDisabled ?? (isPending && !disabled)}
      nativeButton={shouldUseNativeButton}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 flex scale-105
          items-center justify-center opacity-0 transition-[opacity,scale]
          duration-200 group-data-pending/button:scale-100
          group-data-pending/button:opacity-100"
      >
        {/* Spins only while pending: a hidden spinner still costs a style recalc every frame. */}
        <IconLoader2 className="size-4 group-data-pending/button:animate-spin" />
      </span>
      <div
        className={cn(
          `button-content flex items-center gap-2
          transition-[filter,opacity,scale] duration-200
          group-data-pending/button:scale-95
          group-data-pending/button:opacity-30
          group-data-pending/button:blur-[2px]`,
          contentClassName,
        )}
      >
        {children}
      </div>
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
